import React, { useCallback, useContext, useMemo } from 'react'

// components
import Image from 'next/image'
import { IMAGE_PATHS_V2 } from '@/shared/consts/imagesPaths'
import { ButtonComponent } from '@/components/commons/buttons/ButtonComponent'
import { ThemeContext, UserContext } from 'context'
import { ConnectionsModalContext } from '../../../../context/connectionsModal/ConnectionsModalContext'
import { ProfilePageContext } from '../../../../context/profilePage/ProfilePageContext'
import Link from 'next/link'
import { ProfileAvatar } from './personalInfo/ProfileAvatar.component'

import {
  createConnection,
  declineConnection,
  deleteConnection,
} from '@/services/connections'

// styles
import { usernameContainer } from 'styles/v2/pages/profile'
import { useMutation } from '@/shared/hooks/useMutation'
import { ChatSendMessageToastContext } from '@/components/chat/ChatSendMessageToast/ChatSendMessageToastProvider'
import { DropdownMenu, IconButton } from '@/components/commons'
import { useConfirmationModal } from '@/components/confirmationModal/ConfirmationModal'
import { RequestConnectionButton } from '@/components/requestConnectionButton/RequestConnectionButton'
import {
  profileConnectionsLink,
  usernameStyle,
} from './styles/profilePersonalInfo.css'
import {
  BlockConnectionModal,
  BlockConnectionModalTrigger,
} from '@/components/blockConnectionModal/BlockConnectionModal'
import { Text } from '@/components/commons/typography/Text'
import { validateConnectionExistsById } from '@/components/requestConnectionButton/validateConnectionSentReceived'

export const ProfilePersonalInfo: React.FC = () => {
  const { profile, ownProfile } = React.useContext(ProfilePageContext)
  const { toggleModal } = React.useContext(ConnectionsModalContext)
  const { theme } = React.useContext(ThemeContext)

  const editable = false

  return (
    <>
      <ProfileAvatar />

      {profile?.title && (
        <Text
          className="text-center--tablet mt-20--tablet mt-28"
          sizeClass="text-14"
        >
          {profile?.title}
        </Text>
      )}

      <div className={usernameContainer}>
        <h2 className="text-18 font-medium m-0 text-primary text-20--tablet">
          {profile?.firstName} {profile?.lastName}
        </h2>
      </div>

      <div className={`text-16 m-0 text-18--tablet ${usernameStyle}`}>
        {profile?.username}
      </div>

      {/* <p className={creatorTypeClass}>{creatorType}</p> */}
      <div
        className="mt-8 mb-24 mb-16--tablet flex gap-6"
        onClick={toggleModal}
      >
        <Image
          key={theme}
          src={IMAGE_PATHS_V2.icons.commons + theme + '/profiles_icon.svg'}
          alt="profiles icons"
          width={16}
          height={16}
        />
        <Link
          href={`/${profile?.username.toLocaleLowerCase()}/network/connections`}
        >
          <p
            className={`text-16 m-0 text-left text-center--desktop text-secondary ${profileConnectionsLink}`}
          >
            {profile?.connections?.length.toLocaleString() || 0} Connections
          </p>
        </Link>
      </div>

      {!ownProfile && (
        <div className="mb-md full-w flex flex-column items-center justify-center">
          <RequestConnectionButton />
          <MessageConnectionButton />

          <AcceptConnectRequestButton />
          <DeclineConnectionButton />
        </div>
      )}

      {editable && <ButtonComponent label="Edit" />}
    </>
  )
}

const AcceptConnectRequestButton = () => {
  const { user, setUser } = React.useContext(UserContext)
  const { profile, ownProfile } = React.useContext(ProfilePageContext)
  const { connectionsReceived = [] } = user
  const [mutate, { isLoading }] = useMutation({
    mutateMethod: createConnection,
  })
  const onClick = useCallback(async () => {
    const res = await mutate(profile.id)
    if ('error' in res) return
    if (res?.id) {
      setUser(res)
    }
  }, [profile, mutate, setUser])
  const isVisible = useMemo(() => {
    return validateConnectionExistsById({
      id: profile.id,
      connections: connectionsReceived,
    })
  }, [connectionsReceived, profile])
  if (!!ownProfile || !isVisible) return null
  return (
    <ButtonComponent
      label="Accept"
      size="xSmall"
      fullWidth
      loading={isLoading}
      disabled={isLoading}
      onClick={onClick}
    />
  )
}
const MessageConnectionButton = () => {
  const { user, setUser } = React.useContext(UserContext)
  const { profile, ownProfile } = React.useContext(ProfilePageContext)
  const { connections = [] } = user
  const [removeConnection] = useMutation({
    mutateMethod: deleteConnection,
  })
  const { showToast } = useContext(ChatSendMessageToastContext)
  const onClickMessage = useCallback(async () => {
    showToast([profile])
  }, [profile, showToast])

  const showConfirmation = useConfirmationModal({
    title: 'Remove Connection',
    message: 'Are you sure that you want to remove this connection?',
  })
  const onClickRemove = useCallback(async () => {
    const confirmation = await showConfirmation()
    if (!confirmation) return
    const res = await removeConnection(profile.id)
    if ('error' in res) return
    if (res?.id) {
      setUser(res)
    }
  }, [profile, removeConnection, setUser, showConfirmation])
  const isVisible = useMemo(() => {
    return connections.includes(profile.id)
  }, [connections, profile])
  if (!!ownProfile || !isVisible) return null
  return (
    <div
      className="flex flex-row gap-10 items-center justify-stretch"
      style={{ width: '100%' }}
    >
      <ButtonComponent
        label="Message"
        variation="secondary"
        size="xSmall"
        fullWidth
        onClick={onClickMessage}
      />

      <DropdownMenu>
        <DropdownMenu.Trigger>
          <IconButton
            style={{ transform: 'rotate(90deg)' }}
            icon="ThreeDotsIcon"
          />
        </DropdownMenu.Trigger>
        <BlockConnectionModal blockedUserId={profile.id}>
          <DropdownMenu.Content align="end" sideOffset={20} alignOffset={-10}>
            <DropdownMenu.Item onClick={onClickRemove}>
              <span>Remove Connection</span>
            </DropdownMenu.Item>
            <BlockConnectionModalTrigger>
              <DropdownMenu.Item>
                <span>Block Connection</span>
              </DropdownMenu.Item>
            </BlockConnectionModalTrigger>
          </DropdownMenu.Content>
        </BlockConnectionModal>
      </DropdownMenu>
    </div>
  )
}
const DeclineConnectionButton = () => {
  const { user, setUser } = React.useContext(UserContext)
  const { profile, ownProfile } = React.useContext(ProfilePageContext)
  const { connectionsReceived = [] } = user
  const [mutate, { isLoading }] = useMutation({
    mutateMethod: declineConnection,
  })
  const onClick = useCallback(async () => {
    const res = await mutate(profile.id)
    if ('error' in res) return
    if (res?.id) {
      setUser(res)
    }
  }, [profile, mutate, setUser])
  const isVisible = useMemo(() => {
    return validateConnectionExistsById({
      id: profile.id,
      connections: connectionsReceived,
    })
  }, [connectionsReceived, profile])
  if (!!ownProfile || !isVisible) return null
  return (
    <ButtonComponent
      label="Decline"
      variation="secondary"
      size="xSmall"
      loading={isLoading}
      disabled={isLoading}
      fullWidth
      className="mt-10"
      onClick={onClick}
    />
  )
}
